import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";

const APP_BASE = process.env.REACT_APP_APP_URL || "https://app.crednx.com";
import { Menu, X, ArrowUpRight, Sparkles, Building2, ScrollText, Layers, CreditCard, ChevronDown } from "lucide-react";
import { Logo } from "@/components/layout/Logo";
import { Button } from "@/components/ui/button";
import { ThemeToggle } from "@/components/theme-toggle";
import { cn } from "@/lib/utils";

const NAV_LINKS = [
    { label: "Pricing", href: "/pricing" },
    { label: "About", href: "/about" },
    { label: "Contact", href: "/contact" },
];

const PRODUCTS = [
    { label: "Bank Statement Analysis", href: "/products/bsa", desc: "Standardise statements from any bank", icon: Building2, live: true },
    { label: "Credit Bureau Analysis", href: "#", desc: "CIBIL / Experian report parsing", icon: CreditCard, live: false },
    { label: "Invoice Intelligence", href: "#", desc: "B2B invoice extraction & verification", icon: ScrollText, live: false },
    { label: "Underwriting Workspace", href: "#", desc: "Sessions, borrowers, decisions", icon: Layers, live: false },
];

export default function Navbar() {
    const [scrolled, setScrolled] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [productsOpen, setProductsOpen] = useState(false);
    const { pathname } = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 12);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setMobileOpen(false);
        setProductsOpen(false);
    }, [pathname]);

    return (
        <header
            className={cn(
                "sticky top-0 z-50 w-full transition-all duration-300",
                scrolled
                    ? "backdrop-blur-xl bg-background/80 border-b border-border/70"
                    : "bg-transparent border-b border-transparent"
            )}
        >
            <div className="container max-w-[1240px] mx-auto px-6">
                <div className="h-16 md:h-[72px] flex items-center justify-between gap-6">
                    <div className="flex items-center gap-10">
                        <Logo />
                        <nav className="hidden lg:flex items-center gap-1 text-[14px]">
                            <div
                                className="relative"
                                onMouseEnter={() => setProductsOpen(true)}
                                onMouseLeave={() => setProductsOpen(false)}
                            >
                                <button
                                    className={cn(
                                        "px-3 py-2 rounded-md inline-flex items-center gap-1.5 text-foreground/75 hover:text-foreground transition-colors",
                                        productsOpen && "text-foreground"
                                    )}
                                >
                                    Products
                                    <ChevronDown className={cn("w-3.5 h-3.5 transition-transform", productsOpen && "rotate-180")} />
                                </button>
                                {productsOpen && (
                                    <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3 w-[640px]">
                                        <div className="rounded-2xl bg-card border border-border shadow-lg p-3 animate-fade-in">
                                            <div className="grid grid-cols-2 gap-1">
                                                {PRODUCTS.map((p) => (
                                                    <Link
                                                        key={p.label}
                                                        to={p.href}
                                                        className="group flex gap-3 p-3 rounded-xl hover:bg-muted transition-colors"
                                                    >
                                                        <div className="shrink-0 w-9 h-9 rounded-lg bg-primary/5 border border-primary/10 inline-flex items-center justify-center text-primary">
                                                            <p.icon className="w-4 h-4" />
                                                        </div>
                                                        <div className="min-w-0">
                                                            <div className="flex items-center gap-2">
                                                                <span className="text-[13.5px] font-medium text-foreground">{p.label}</span>
                                                                {p.live ? (
                                                                    <span className="text-[10px] tracking-wider font-semibold uppercase px-1.5 py-0.5 rounded bg-accent/30 text-accent-foreground">
                                                                        Live
                                                                    </span>
                                                                ) : (
                                                                    <span className="text-[10px] tracking-wider font-medium uppercase px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                                                                        Soon
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <p className="text-[12.5px] text-muted-foreground mt-0.5 leading-snug">{p.desc}</p>
                                                        </div>
                                                    </Link>
                                                ))}
                                            </div>
                                            <div className="mt-2 px-3 py-2.5 rounded-xl bg-primary/[0.04] border border-primary/10 flex items-center gap-3">
                                                <Sparkles className="w-4 h-4 text-primary" />
                                                <p className="text-[12.5px] text-muted-foreground">
                                                    New: Bank Statement Analysis is live in 5 markets.
                                                </p>
                                                <Link to="/products/bsa" className="ml-auto text-[12.5px] font-semibold text-primary inline-flex items-center gap-1">
                                                    Explore <ArrowUpRight className="w-3.5 h-3.5" />
                                                </Link>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                            {NAV_LINKS.map((l) => (
                                <Link
                                    key={l.href}
                                    to={l.href}
                                    className={cn(
                                        "px-3 py-2 rounded-md text-foreground/75 hover:text-foreground transition-colors",
                                        pathname === l.href && "text-foreground"
                                    )}
                                >
                                    {l.label}
                                </Link>
                            ))}
                        </nav>
                    </div>

                    <div className="hidden lg:flex items-center gap-2">
                        <ThemeToggle />
                        <Button variant="ghost" size="sm" asChild>
                            <a href={`${APP_BASE}/login`} data-testid="navbar-sign-in-link">Sign in</a>
                        </Button>
                        <Button variant="hero" size="sm" asChild>
                            <Link to="/contact">
                                Get a demo
                                <ArrowUpRight className="w-3.5 h-3.5 ml-1" />
                            </Link>
                        </Button>
                    </div>

                    <div className="lg:hidden flex items-center gap-1">
                        <ThemeToggle />
                        <button
                            className="inline-flex items-center justify-center w-10 h-10 rounded-md text-foreground/80 hover:bg-muted"
                            onClick={() => setMobileOpen((v) => !v)}
                            aria-label="Toggle menu"
                        >
                            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                        </button>
                    </div>
                </div>
            </div>

            {mobileOpen && (
                <div className="lg:hidden border-t border-border bg-background/95 backdrop-blur-xl">
                    <div className="container max-w-[1240px] mx-auto px-6 py-4 flex flex-col gap-1">
                        <p className="text-[11px] uppercase tracking-widest text-muted-foreground px-3 pt-2 pb-1">Products</p>
                        {PRODUCTS.map((p) => (
                            <Link key={p.label} to={p.href} className="px-3 py-2.5 rounded-md text-[14.5px] text-foreground/85 hover:bg-muted flex items-center gap-2">
                                <p.icon className="w-4 h-4 text-primary" /> {p.label}
                                {!p.live && <span className="text-[10px] uppercase tracking-wider text-muted-foreground ml-auto">Soon</span>}
                            </Link>
                        ))}
                        <div className="editorial-rule my-2" />
                        {NAV_LINKS.map((l) => (
                            <Link key={l.href} to={l.href} className="px-3 py-2.5 rounded-md text-[14.5px] text-foreground/85 hover:bg-muted">
                                {l.label}
                            </Link>
                        ))}
                        <div className="editorial-rule my-2" />
                        <div className="flex gap-2 px-1 pb-1">
                            <Button variant="outline" size="sm" asChild className="flex-1">
                                <a href={`${APP_BASE}/login`} data-testid="navbar-mobile-sign-in-link">Sign in</a>
                            </Button>
                            <Button variant="hero" size="sm" asChild className="flex-1">
                                <Link to="/contact">Get a demo</Link>
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </header>
    );
}
